import { useEffect, useState } from "react";

import type { PolyfutureApiClient, PortfolioListKind, PortfolioListRow } from "./api.js";
import { ProductHeading, ProductPage } from "./ProductSurface.js";

export interface AcrossPortfolioChoice {
  readonly id: string;
  readonly name: string;
}

const PAGES: Record<
  PortfolioListKind,
  { readonly title: string; readonly body: string; readonly empty: string; readonly item: string }
> = {
  research_notes: {
    title: "Research notes",
    body: "Investigations opened from alerts, in every portfolio. The most urgent come first.",
    empty: "No research notes yet. They start when an alert needs a closer look.",
    item: "Note",
  },
  reports: {
    title: "Reports",
    body: "Reports drafted, approved or published for every portfolio.",
    empty: "No reports yet. A report is drafted from a portfolio's research notes.",
    item: "Report",
  },
  analysis: {
    title: "Analysis activity",
    body: "What-if and coverage runs in every portfolio, newest first.",
    empty: "No analysis has run yet.",
    item: "Run",
  },
  episodes: {
    title: "Episodes",
    body: "The monitoring record: every alert episode in every portfolio, open ones first.",
    empty: "No alert has opened an episode yet.",
    item: "Alert rule",
  },
  reviews: {
    title: "Reviews & approvals",
    body: "Suggested assumptions waiting for a decision, and assumptions whose review date has passed, in every portfolio.",
    empty: "Nothing is waiting for review.",
    item: "Needs review",
  },
};

const STATUS_LABELS: Readonly<Record<string, string>> = {
  open: "Open",
  acknowledged: "Acknowledged",
  investigating: "Investigating",
  escalated: "Escalated",
  resolved: "Resolved",
  draft: "Draft",
  approved: "Approved",
  published: "Published",
  queued: "Queued",
  running: "Running",
  partial: "Partly complete",
  complete: "Complete",
  failed: "Failed",
  awaiting_decision: "Awaiting a decision",
  review_due: "Review date passed",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.replaceAll("_", " ");
}

/**
 * V-S1d. A page under More across every portfolio: one row per item with its portfolio. Choosing
 * a portfolio in the filter opens that portfolio's own page, where items are created.
 */
export function AcrossPortfoliosView({
  client,
  kind,
  portfolios,
  onOpenRow,
  onOpenPortfolio,
  embedded = false,
}: {
  readonly client: PolyfutureApiClient;
  readonly kind: PortfolioListKind;
  readonly portfolios: readonly AcrossPortfolioChoice[];
  readonly onOpenRow: (row: PortfolioListRow) => void;
  readonly onOpenPortfolio: (bookId: string) => void;
  /** Shown inside another page (Market research's Episodes), under that page's own heading. */
  readonly embedded?: boolean;
}) {
  const [rows, setRows] = useState<readonly PortfolioListRow[]>();
  const [error, setError] = useState<string>();
  const page = PAGES[kind];

  useEffect(() => {
    let current = true;
    setRows(undefined);
    setError(undefined);
    void client.listAcrossPortfolios(kind).then(
      (listed) => {
        if (current) setRows(listed);
      },
      (caught: unknown) => {
        if (current) setError(caught instanceof Error ? caught.message : "The list did not load.");
      },
    );
    return () => {
      current = false;
    };
  }, [client, kind]);

  const listing = (
    <>
      {error !== undefined ? (
        <p role="alert" className="error-banner">
          {error}
        </p>
      ) : rows === undefined ? (
        <p role="status">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="panel">{page.empty}</p>
      ) : (
        <div className="panel across-portfolios-table" tabIndex={0}>
          <table>
            <caption className="visually-hidden">{page.title} across portfolios</caption>
            <thead>
              <tr>
                <th scope="col">{page.item}</th>
                <th scope="col">Portfolio</th>
                <th scope="col">Status</th>
                <th scope="col">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.target}:${row.id}`}>
                  <td>
                    <button type="button" className="link-button" onClick={() => onOpenRow(row)}>
                      {row.title}
                    </button>
                  </td>
                  <td>{row.bookName}</td>
                  <td>{statusLabel(row.status)}</td>
                  <td>
                    <time dateTime={row.at}>{new Date(row.at).toLocaleDateString()}</time>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
  if (embedded) {
    return (
      <section className="across-portfolios" aria-label={page.title}>
        <p className="pf-markets-muted">{page.body}</p>
        {listing}
      </section>
    );
  }
  return (
    <ProductPage className="across-portfolios">
      <ProductHeading
        eyebrow="Every portfolio"
        title={page.title}
        body={<p>{page.body}</p>}
        actions={
          portfolios.length > 1 ? (
            <label className="across-portfolios-filter">
              <span>Portfolio</span>
              <select
                value=""
                onChange={(event) => {
                  if (event.target.value !== "") onOpenPortfolio(event.target.value);
                }}
              >
                <option value="">All portfolios</option>
                {portfolios.map((portfolio) => (
                  <option value={portfolio.id} key={portfolio.id}>
                    {portfolio.name}
                  </option>
                ))}
              </select>
            </label>
          ) : portfolios.length === 1 ? (
            <button
              className="button"
              type="button"
              onClick={() => onOpenPortfolio(portfolios[0]!.id)}
            >
              Open {portfolios[0]!.name}
            </button>
          ) : null
        }
      />
      {listing}
    </ProductPage>
  );
}
